import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  Chip,
  Paper,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Divider
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CommentRoundedIcon from '@mui/icons-material/CommentRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { adminAPI, crmAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  CATEGORY_LABEL,
  UPCOMING_REGISTRATION_TYPE,
  formatLeadDate,
  leadStatusLabel,
  resolveLeadDisplay
} from '../utils/leadRequestDisplay.js';
import { REGISTRATION_LEAD_STATUS_LABELS } from '../constants/registrationLeadStatuses.js';
import { StatusChip } from './ui';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

function statusDisplay(status) {
  if (!status) return 'Not set';
  return REGISTRATION_LEAD_STATUS_LABELS[status] || status;
}

function DetailRow({ label, children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
        gap: { xs: 0.25, sm: 2 },
        py: 0.85,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Box sx={{ wordBreak: 'break-word', fontSize: '13.5px' }}>{children ?? '—'}</Box>
    </Box>
  );
}

function LeadDetailFields({ lead, booking, bookingLoading }) {
  const { packageName, eventDate, trekEvent, registrationStatus, isUpcomingReg } = resolveLeadDisplay(
    lead,
    booking
  );

  return (
    <Box sx={{ pt: 0.5 }}>
      <DetailRow label="Category">
        <StatusChip
          status={lead.leadType}
          label={CATEGORY_LABEL[lead.leadType] || lead.leadType || '—'}
        />
      </DetailRow>
      <DetailRow label="Name">{lead.name || '—'}</DetailRow>
      <DetailRow label="Phone">{lead.phone || booking?.registration?.phone || '—'}</DetailRow>
      <DetailRow label="Trek/Event">{trekEvent}</DetailRow>
      {isUpcomingReg && bookingLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading package…
          </Typography>
        </Box>
      ) : (
        <DetailRow label="Package">{isUpcomingReg ? packageName : '—'}</DetailRow>
      )}
      <DetailRow label="Date of event">{eventDate}</DetailRow>
      <DetailRow label="Reason">{lead.reason || '—'}</DetailRow>
      <DetailRow label="Lead Date">{formatDateTime(lead.createdAt || booking?.createdAt)}</DetailRow>
      {isUpcomingReg ? (
        <DetailRow label="Registration status">
          <StatusChip status={booking?.registration?.status} label={registrationStatus} />
        </DetailRow>
      ) : null}
      <DetailRow label="Lead status">
        <StatusChip status={lead.leadStatus} label={leadStatusLabel(lead)} />
      </DetailRow>
      <DetailRow label="Assignee">{lead.assignee?.name || '—'}</DetailRow>
    </Box>
  );
}

export default function LeadRequestViewDialog({ open, lead, onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  const [newNoteText, setNewNoteText] = useState('');
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const isUpcomingReg = lead?.leadType === UPCOMING_REGISTRATION_TYPE;
  const bookingId = lead?.bookingId;

  const { data: bookingRes, isLoading: bookingLoading } = useQuery({
    queryKey: ['lead-request-view-booking', bookingId],
    queryFn: () => adminAPI.getBooking(bookingId),
    enabled: open && isUpcomingReg && !!bookingId
  });

  const { data: activityRes, isLoading: activityLoading } = useQuery({
    queryKey: ['lead-activity', lead?.leadType, lead?.id, lead?.phone],
    queryFn: () => crmAPI.getLeadActivity(lead.leadType, lead.id, { phone: lead.phone }),
    enabled: open && !!lead?.leadType && lead?.id != null
  });

  const addNoteMutation = useMutation(
    (text) => crmAPI.appendLeadNote(lead.leadType, lead.id, { note: text }),
    {
      onSuccess: () => {
        setNewNoteText('');
        queryClient.invalidateQueries(['lead-activity', lead?.leadType, lead?.id, lead?.phone]);
        queryClient.invalidateQueries(['lead-requests']);
        showToast({ message: 'Comment added.', severity: 'success' });
      },
      onError: (err) =>
        showToast({
          message: err?.response?.data?.message || 'Failed to add comment',
          severity: 'error'
        })
    }
  );

  const handleAddNote = (e) => {
    e.preventDefault();
    const t = newNoteText.trim();
    if (!t) return;
    addNoteMutation.mutate(t);
  };

  const booking = bookingRes?.data?.data;

  // Inquiries for this customer
  const inquiryList = useMemo(() => {
    const remoteInquiries = activityRes?.data?.data?.inquiries;
    if (Array.isArray(remoteInquiries) && remoteInquiries.length > 0) {
      return remoteInquiries;
    }
    if (Array.isArray(lead?.samePhoneInquiries) && lead.samePhoneInquiries.length > 0) {
      return lead.samePhoneInquiries;
    }
    return lead ? [lead] : [];
  }, [activityRes?.data?.data?.inquiries, lead]);

  // Status changes history
  const statusEvents = useMemo(() => {
    const events = activityRes?.data?.data?.statusEvents || [];
    return [...events].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [activityRes?.data?.data?.statusEvents]);

  // Notes / Comment history
  const displayNotes = useMemo(() => {
    const rawNotes = activityRes?.data?.data?.notes || [];
    const notes = [...rawNotes];
    const legacy = String(lead?.message || booking?.registration?.registrationComment || '').trim();
    if (legacy) {
      const alreadyLogged = notes.some((n) => String(n.body || '').trim() === legacy);
      if (!alreadyLogged) {
        notes.push({
          id: 'initial-inquiry-message',
          body: legacy,
          createdByName: `${lead?.name && lead.name !== '—' ? lead.name : 'Customer'} (Inquiry message)`,
          createdAt: lead?.createdAt || booking?.createdAt || null
        });
      }
    }
    return notes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [activityRes?.data?.data?.notes, lead?.message, lead?.name, lead?.createdAt, booking]);

  const title = lead?.name && lead.name !== '—' ? `View — ${lead.name}` : 'View lead';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          fontWeight: 700,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <span>{title}</span>
          {inquiryList.length > 1 ? (
            <Chip
              size="small"
              label={`${inquiryList.length} inquiries`}
              color="warning"
              sx={{ height: 22, fontSize: '11px', fontWeight: 600 }}
            />
          ) : null}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44 }}
        >
          <Tab
            icon={<InfoOutlinedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Overview"
            sx={{ minHeight: 44, fontSize: '13px', textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<FormatListBulletedRoundedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label={`Inquiries (${inquiryList.length})`}
            sx={{ minHeight: 44, fontSize: '13px', textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<HistoryRoundedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label={`Lead History (${statusEvents.length})`}
            sx={{ minHeight: 44, fontSize: '13px', textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<CommentRoundedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label={`Comments (${displayNotes.length})`}
            sx={{ minHeight: 44, fontSize: '13px', textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 2.5 }}>
        {/* TAB 0: OVERVIEW (Details + Quick Summary of Inquiries, Status, Comments) */}
        {activeTab === 0 && lead ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '11.5px' }}>
                Lead Details
              </Typography>
              <LeadDetailFields lead={lead} booking={booking} bookingLoading={bookingLoading && !!bookingId} />
            </Box>

            {/* Quick Inquiries Preview if > 1 */}
            {inquiryList.length > 1 ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '11.5px' }}>
                    Customer Inquiries ({inquiryList.length})
                  </Typography>
                  <Button size="small" variant="text" onClick={() => setActiveTab(1)} sx={{ fontSize: '12px', py: 0 }}>
                    View all inquiries →
                  </Button>
                </Box>
                <Stack spacing={1}>
                  {inquiryList.slice(0, 3).map((inq, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: inq.id === lead.id ? '#f0fdf4' : 'background.paper',
                        borderColor: inq.id === lead.id ? '#86efac' : 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {inq.eventLabel || inq.eventSlug || 'General Inquiry'}
                          </Typography>
                          {inq.id === lead.id ? (
                            <Chip size="small" label="Current" color="success" sx={{ height: 18, fontSize: '10px', fontWeight: 600 }} />
                          ) : null}
                        </Box>
                        <StatusChip status={inq.leadType} label={CATEGORY_LABEL[inq.leadType] || inq.source || inq.leadType} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
                          {formatDateTime(inq.createdAt)}
                        </Typography>
                        <StatusChip status={inq.leadStatus} label={statusDisplay(inq.leadStatus)} />
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ) : null}

            {/* Quick Recent Comments & Status */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {/* Latest Status Change */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '11.5px' }}>
                    Latest Status Change
                  </Typography>
                  {statusEvents.length > 0 ? (
                    <Button size="small" variant="text" onClick={() => setActiveTab(2)} sx={{ fontSize: '11.5px', py: 0 }}>
                      History ({statusEvents.length}) →
                    </Button>
                  ) : null}
                </Box>
                {statusEvents.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                      <StatusChip status={statusEvents[0].fromStatus} label={statusDisplay(statusEvents[0].fromStatus)} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>→</Typography>
                      <StatusChip status={statusEvents[0].toStatus} label={statusDisplay(statusEvents[0].toStatus)} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      By <strong>{statusEvents[0].changedByName || 'System'}</strong> · {formatDateTime(statusEvents[0].createdAt)}
                    </Typography>
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '13px' }}>
                    No status changes recorded yet.
                  </Typography>
                )}
              </Box>

              {/* Latest Comment */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '11.5px' }}>
                    Latest Comment
                  </Typography>
                  {displayNotes.length > 0 ? (
                    <Button size="small" variant="text" onClick={() => setActiveTab(3)} sx={{ fontSize: '11.5px', py: 0 }}>
                      Comments ({displayNotes.length}) →
                    </Button>
                  ) : null}
                </Box>
                {displayNotes.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {displayNotes[0].createdByName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(displayNotes[0].createdAt)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                      {displayNotes[0].body}
                    </Typography>
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '13px' }}>
                    No comments or notes recorded yet.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ) : null}

        {/* TAB 1: INQUIRY HISTORY */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Inquiry History ({inquiryList.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All requests, brochure downloads, calls, and registrations from phone {lead?.phone || '—'}:
              </Typography>
            </Box>

            {inquiryList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No inquiry history found.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {inquiryList.map((inq, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: inq.id === lead?.id ? '#f0fdf4' : 'background.paper',
                      borderColor: inq.id === lead?.id ? '#86efac' : 'divider',
                      boxShadow: inq.id === lead?.id ? '0 1px 3px rgba(34, 197, 94, 0.15)' : 'none'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '14px' }}>
                            {inq.eventLabel || inq.eventSlug || 'General Inquiry'}
                          </Typography>
                          {inq.id === lead?.id ? (
                            <Chip size="small" label="Current Lead" color="success" sx={{ height: 20, fontSize: '10.5px', fontWeight: 600 }} />
                          ) : null}
                        </Box>
                        {inq.reason ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '12.5px' }}>
                            <strong>Reason:</strong> {inq.reason}
                          </Typography>
                        ) : null}
                        {inq.message ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '12.5px', whiteSpace: 'pre-wrap' }}>
                            <strong>Message:</strong> {inq.message}
                          </Typography>
                        ) : null}
                      </Box>
                      <StatusChip status={inq.leadType} label={CATEGORY_LABEL[inq.leadType] || inq.source || inq.leadType} />
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                        <AccessTimeRoundedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        <span>Submitted on <strong>{formatDateTime(inq.createdAt)}</strong></span>
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="caption" color="text.secondary">Status:</Typography>
                        <StatusChip status={inq.leadStatus} label={statusDisplay(inq.leadStatus)} />
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* TAB 2: LEAD STATUS CHANGE HISTORY */}
        {activeTab === 2 && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Lead Status History ({statusEvents.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Audit trail of every status transition with exact timestamps and assignee attribution:
              </Typography>
            </Box>

            {activityLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : statusEvents.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" color="text.secondary">
                  No status changes recorded yet for this customer.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.25}>
                {statusEvents.map((event, idx) => (
                  <Paper
                    key={event.id || idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <StatusChip status={event.fromStatus} label={statusDisplay(event.fromStatus)} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>→</Typography>
                        <StatusChip status={event.toStatus} label={statusDisplay(event.toStatus)} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeRoundedIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                        {formatDateTime(event.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Changed by: <strong style={{ color: '#0f172a' }}>{event.changedByName || 'System'}</strong>
                      </Typography>
                      {event.leadType ? (
                        <Chip
                          size="small"
                          label={CATEGORY_LABEL[event.leadType] || event.leadType}
                          sx={{ height: 18, fontSize: '10px' }}
                        />
                      ) : null}
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* TAB 3: COMMENTS & NOTES HISTORY */}
        {activeTab === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Comments &amp; Notes History ({displayNotes.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All telecaller notes and customer comments with timestamps:
              </Typography>
            </Box>

            {/* Quick Add Note Form inside View popup */}
            <Paper variant="outlined" component="form" onSubmit={handleAddNote} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#166534', display: 'block', mb: 0.75 }}>
                Add a quick note / comment:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type a note (e.g. called, requested callback at 6pm)..."
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  disabled={addNoteMutation.isLoading}
                  sx={{ bgcolor: 'background.paper' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={!newNoteText.trim() || addNoteMutation.isLoading}
                  startIcon={addNoteMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{ px: 2, whiteSpace: 'nowrap' }}
                >
                  Post Note
                </Button>
              </Box>
            </Paper>

            {activityLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : displayNotes.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="body2" color="text.secondary">
                  No comments or notes recorded yet.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.25}>
                {displayNotes.map((note, idx) => (
                  <Paper
                    key={note.id || idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      borderColor: '#e2e8f0'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '13px' }}>
                        {note.createdByName || 'Admin'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
                        {formatDateTime(note.createdAt)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', fontSize: '13.5px' }}>
                      {note.body}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
